import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const ROTATING_WORDS = ['Athletes', 'Programs', 'Fans'];
const ROTATION_MS = 2400;

/**
 * FlytTaglineHero — top-of-page hero for /flyt.
 *   "We build the stories [Athletes ▸ Programs ▸ Fans] want told."
 * The final word rotates every ~2.5s with a soft slide+fade swap.
 */
const FlytTaglineHero = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % ROTATING_WORDS.length), ROTATION_MS);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      className="relative bg-black text-white overflow-hidden"
      data-testid="flyt-tagline-hero"
    >
      {/* Soft yellow glow background */}
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 15% 25%, rgba(240,216,117,0.45), transparent 55%), radial-gradient(circle at 85% 80%, rgba(212,175,55,0.30), transparent 55%)',
        }}
      />
      {/* Subtle film-grain noise overlay */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.55'/></svg>\")",
        }}
      />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <p className="font-italic text-yellow-300 text-xs sm:text-sm tracking-[0.25em] uppercase mb-5">
          FLYT Studios
        </p>
        <h1 className="font-display text-[44px] leading-[0.92] sm:text-6xl lg:text-7xl xl:text-[88px] uppercase">
          We build the stories<br />
          <span className="relative inline-block align-baseline pr-2" data-testid="flyt-tagline-rotating-word">
            <span
              key={ROTATING_WORDS[idx]}
              className="gold-gradient inline-block"
              style={{ animation: 'taglineSwap 700ms cubic-bezier(0.22,1,0.36,1)' }}
            >
              {ROTATING_WORDS[idx]}
            </span>
          </span>
          want told.
        </h1>
        <p className="mt-6 text-base sm:text-lg text-gray-300 leading-relaxed max-w-2xl">
          We help brands integrate naturally into athlete stories, original series, and sports-driven content ecosystems &mdash; never traditional advertising.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/flyt/case-studies"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-yellow-300 hover:bg-yellow-200 text-black text-xs font-bold uppercase tracking-[0.18em] transition group"
            data-testid="flyt-tagline-see-work"
          >
            See the work <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
          </Link>
          <Link
            to="/flyt/brands"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full ring-1 ring-white/20 hover:ring-white text-white text-xs font-bold uppercase tracking-[0.18em] transition"
            data-testid="flyt-tagline-partner"
          >
            Partner with us
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FlytTaglineHero;
